import { Fragment, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";

/**
 * PortalShell — sidebar + main-content layout for merchant, admin, reseller portals.
 * variant "cream" (light glass) | "burgundy" (dark console). Below lg the sidebar becomes a drawer.
 */
function linkClass(isBurgundy, isActive) {
  if (isBurgundy) return `console-burgundy__link ${isActive ? "console-burgundy__link--active" : ""}`;
  return `px-3 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors ${
    isActive ? "pf-btn-primary shadow-md" : "hover:bg-white/60 text-slate-600 hover:text-slate-900"}`;
}

function NavItems({ links, isBurgundy, suffix = "", onPick }) {
  return (
    <nav className="mt-6 flex flex-col gap-1">
      {links.map((l) => (
        <Fragment key={l.to}>
        {l.section && (
          <div className={`mt-4 mb-1 px-3 text-[10px] uppercase tracking-[0.2em] font-semibold ${isBurgundy ? "console-burgundy__eyebrow" : "text-slate-400"}`}
            data-testid={`${l.testid}${suffix}-section`}>{l.section}</div>
        )}
        <NavLink to={l.to} end={l.end} onClick={onPick} className={({ isActive }) => linkClass(isBurgundy, isActive)}
          data-testid={`${l.testid}${suffix}`}>
          <l.icon className="w-4 h-4 flex-shrink-0" />
          <span className="min-w-0">{l.label}</span>
          {l.badge && (
            <span className={`ml-auto shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${isBurgundy ? "bg-white/10 text-emerald-200" : "bg-cyan-100 text-cyan-800"}`}
              data-testid={`${l.testid}${suffix}-badge`}>{l.badge}</span>
          )}
          {l.count > 0 && (
            <span className={`${l.badge ? "ml-1" : "ml-auto"} min-w-[20px] h-5 px-1.5 rounded-full bg-rose-600 text-white text-[11px] font-bold inline-flex items-center justify-center tabular-nums`}
              data-testid={`${l.testid}${suffix}-count`} aria-label={`${l.count} waiting`}>{l.count > 99 ? "99+" : l.count}</span>
          )}
        </NavLink>
        </Fragment>
      ))}
    </nav>
  );
}

function Heading({ title, subtitle, isBurgundy }) {
  return (
    <>
      <div className={`text-xs uppercase tracking-[0.2em] font-semibold ${isBurgundy ? "console-burgundy__eyebrow" : "text-slate-500"}`}>{subtitle}</div>
      <h1 className={`font-display text-2xl font-medium tracking-tight mt-1 ${isBurgundy ? "console-burgundy__title" : "text-slate-900"}`}>{title}</h1>
    </>
  );
}

function MobileBar({ title, subtitle, links, testid, isBurgundy }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const current = links.filter((l) => (l.end ? pathname === l.to : pathname.startsWith(l.to)))
    .sort((a, b) => b.to.length - a.to.length)[0];
  const box = isBurgundy ? "console-burgundy" : "glass-strong";
  return (
    <div className={`lg:hidden rounded-2xl p-4 flex items-center justify-between gap-3 ${box}`} data-testid={`${testid}-mobile-bar`}>
      <div className="min-w-0"><Heading title={title} subtitle={subtitle} isBurgundy={isBurgundy} /></div>
      <button type="button" onClick={() => setOpen(true)} aria-label={`Open ${title} menu`} data-testid={`${testid}-menu-btn`}
        className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${isBurgundy ? "bg-white/10 text-white hover:bg-white/20" : "pf-btn-glass"}`}>
        <Menu className="w-4 h-4" /> <span className="max-w-[140px] truncate">{current?.label || "Menu"}</span>
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 overflow-y-auto w-[85vw] max-w-xs" data-testid={`${testid}-drawer`}>
          <div className={`min-h-full p-5 ${box}`}>
            <SheetTitle asChild><div><Heading title={title} subtitle={subtitle} isBurgundy={isBurgundy} /></div></SheetTitle>
            <SheetDescription className="sr-only">{title} navigation</SheetDescription>
            <NavItems links={links} isBurgundy={isBurgundy} suffix="-mobile" onPick={() => setOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default function PortalShell({ title, subtitle, links, children, testid, variant = "cream" }) {
  const isBurgundy = variant === "burgundy";
  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 lg:py-10 grid grid-cols-1 lg:grid-cols-[256px_minmax(0,1fr)] gap-6 lg:gap-8 relative z-10" data-testid={testid}>
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="blob blob-cyan blob-float w-[500px] h-[500px] -top-40 -left-40 opacity-40" />
        <div className="blob blob-lav blob-float w-[500px] h-[500px] top-1/2 -right-40 opacity-30" style={{ animationDelay: "5s" }} />
      </div>
      <MobileBar title={title} subtitle={subtitle} links={links} testid={testid} isBurgundy={isBurgundy} />
      <aside className={`hidden lg:block lg:sticky lg:top-24 h-fit rounded-3xl p-5 ${isBurgundy ? "console-burgundy" : "glass-strong"}`} data-testid={`${testid}-sidebar`}>
        <Heading title={title} subtitle={subtitle} isBurgundy={isBurgundy} />
        <NavItems links={links} isBurgundy={isBurgundy} />
      </aside>
      <main className="relative min-w-0">{children}</main>
    </div>
  );
}
